import {
  getTemplateDesign,
  normalizeDesign,
  type DividerSegment,
  type HandlePlacement,
  type StorageDesign,
  type TemplateId
} from "./design";

const templateIds: TemplateId[] = ["basic-box", "tcg-card-box", "cutlery-tray"];

export function parseStorageDesignJson(value: unknown): StorageDesign {
  if (!isRecord(value)) {
    throw new Error("Design file must contain a JSON object.");
  }

  if (value.schemaVersion !== 1) {
    throw new Error("Design file schema version is not supported.");
  }

  const templateId = isTemplateId(value.templateId) ? value.templateId : "basic-box";
  const fallback = getTemplateDesign(templateId);
  const merged: StorageDesign = {
    ...fallback,
    ...value,
    schemaVersion: 1,
    templateId,
    hasBottom: booleanOr(value.hasBottom, fallback.hasBottom),
    hasFingerNotch: booleanOr(value.hasFingerNotch, fallback.hasFingerNotch),
    handlePlacements: parseHandlePlacements(value.handlePlacements, fallback.handlePlacements),
    columnFractions: parseNumberArray(value.columnFractions, fallback.columnFractions),
    rowFractions: parseNumberArray(value.rowFractions, fallback.rowFractions),
    dividerSegments: parseDividerSegments(value.dividerSegments, fallback.dividerSegments)
  };

  return normalizeDesign(merged);
}

function parseHandlePlacements(value: unknown, fallback: HandlePlacement[]): HandlePlacement[] {
  if (!Array.isArray(value)) {
    return fallback;
  }

  return value.filter(isRecord).map((placement) => ({
    id: stringOr(placement.id, ""),
    face: stringOr(placement.face, "front") as HandlePlacement["face"],
    offset: numberOr(placement.offset, 0.5),
    verticalOffset: numberOr(placement.verticalOffset, 0.58)
  }));
}

function parseDividerSegments(value: unknown, fallback: DividerSegment[]): DividerSegment[] {
  if (!Array.isArray(value)) {
    return fallback;
  }

  return value.filter(isRecord).map((segment) => ({
    id: stringOr(segment.id, ""),
    axis: stringOr(segment.axis, "column") as DividerSegment["axis"],
    fraction: numberOr(segment.fraction, 0.5),
    startFraction: numberOr(segment.startFraction, 0),
    endFraction: numberOr(segment.endFraction, 1)
  }));
}

function parseNumberArray(value: unknown, fallback: number[]): number[] {
  if (!Array.isArray(value)) {
    return fallback;
  }

  return value.map((item) => numberOr(item, 0.5));
}

function isTemplateId(value: unknown): value is TemplateId {
  return typeof value === "string" && templateIds.includes(value as TemplateId);
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function booleanOr(value: unknown, fallback: boolean): boolean {
  return typeof value === "boolean" ? value : fallback;
}

function numberOr(value: unknown, fallback: number): number {
  return typeof value === "number" && Number.isFinite(value) ? value : fallback;
}

function stringOr(value: unknown, fallback: string): string {
  return typeof value === "string" ? value : fallback;
}
